const regExpSpecialCharacters: RegExp = /[/\-\\^$*+?.()|[\]{}]/g;

/**
 * Determines whether a given string is empty or not by evaluating <code>undefined</code>,
 * <code>null</code> and whitespace.  Note this does runtime type checking.
 *
 * @param toCheck the object to check for emptiness.
 * @param allowWhitespace set to true if the empty string can contain whitespace and still be considered empty;
 * default is false and means that any string only containing whitespace is considered NOT empty.
 *
 * @returns true if the string to check is empty (or not a string type), false otherwise.
 */
export function isEmptyString(toCheck: string | null | undefined | unknown, allowWhitespace: boolean = false): boolean {
  if (toCheck !== undefined && toCheck !== null && typeof toCheck === 'string') {
    if (allowWhitespace) {
      return toCheck.length === 0 || (toCheck.length >= 0 && toCheck.trim().length === 0);
    }

    return toCheck.length === 0;
  }

  // Default is true, as in the case where null, undefined or a non-string object is passed in.
  return true;
}

/**
 * Determines whether a given string is empty or not by evaluating <code>undefined</code>,
 * <code>null</code> and whitespace.  Note this does runtime type checking.
 *
 * @param toCheck the object to check for emptiness.
 * @param allowWhitespace set to true if the empty string can contain whitespace and not be empty; default is false.
 *
 * @returns true if the string to check is NOT an empty string, false otherwise (including if not a string type).
 */
export function isNotEmptyString(
  toCheck: string | null | undefined | unknown,
  allowWhitespace: boolean = false
): boolean {
  return !isEmptyString(toCheck, allowWhitespace);
}

/**
 * Convert raw phone number string into 333-333-4444 format.
 *
 * @param phoneNumber ten character numerical string, not beginning with 0
 *
 * @return empty string if passed falsy phoneNumber, original string if passed
 * something that violates other constraints, new string in 333-333-4444 format
 * if passed a good argument.
 * */
export function getFormattedPhoneNumber(phoneNumber: string | undefined | null): string {
  if (!phoneNumber) return '';
  if (phoneNumber?.length !== 10 || phoneNumber?.at(0) === '0') return phoneNumber;
  return phoneNumber.replace(/([0-9]{3})([0-9]{3})([0-9]{4})/, '$1-$2-$3');
}

/**
 * Determines if a given string only contains numbers.
 *
 * @param toCheck the string to verify.
 *
 * @return true if the given string has only numbers, false otherwise.
 */
export function isNumber(toCheck: string | null | undefined): boolean {
  return (
    toCheck !== undefined &&
    toCheck !== null &&
    toCheck !== '' &&
    !Number.isNaN(toCheck as unknown as number) &&
    !Number.isNaN(parseFloat(toCheck))
  );
}

/**
 * Creates a number-typed value from a string-typed value.
 *
 * @param stringToConvert the string to convert to a number.
 *
 * @return the number format of the given string or NaN if the number cannot be converted.
 */
export function getStringAsNumber(stringToConvert: string | null | undefined): number {
  if (!isNumber(stringToConvert)) {
    return NaN;
  }

  return Number(stringToConvert);
}

/**
 * Converts a given string into an escaped string that can be used for {@link RegExp} matching.
 *
 * @param stringToConvert the string to convert to a valid {@link RegExp} string.
 *
 * @return an escaped and valid {@link RegExp} string.
 */
export function escapeRegularExpressionString(stringToConvert: string | null | undefined): string {
  if (stringToConvert) {
    return stringToConvert.replace(regExpSpecialCharacters, '\\$&');
  } else {
    return '';
  }
}

/**
 * Invalid characters are: apostrophe.
 */
const invalidKebabCaseCharacters: RegExp = /['\u2019]/g;

/**
 * Defines word boundaries by analyzing and grouping ASCII characters.
 */
const kebabCaseWords: RegExp = /[0-9]+|[A-Z]{2,}|[A-Z]+[a-z]*|[a-z]+/g;

/**
 * Creates a kebab case string from the given input.  By default, the string is converted to lower case, but this
 * can be controlled with the <code>toLowerCase</code> parameter.  Examples:
 * <pre>
 *   - 'MyTestString' -> 'my-test-string'
 *   - 'MyTest123String' -> 'my-test-123-string'
 *   - 'MyTESTString' -> 'my-tests-tring'
 * </pre>
 *
 * @param stringToConvert the string to convert to a number.
 * @param toLowerCase true (default) if the string should be converted to all lower case, false if the string
 * should maintain the character case as identified.
 *
 * @return the number format of the given string or NaN if the number cannot be converted.
 */
export function toKebabCase(stringToConvert: string | null | undefined, toLowerCase: boolean = true): string {
  if (isEmptyString(stringToConvert)) {
    return '';
  }

  const words: RegExpMatchArray | [] =
    stringToConvert!.replace(invalidKebabCaseCharacters, '').match(kebabCaseWords) || [];

  if (toLowerCase) {
    return words.join('-').toLowerCase();
  }

  return words.join('-');
}

/**
 * Replaces ONLY the last instance of a given search string in a string, which is not possible to do directly using
 * JavaScript. Note that matching is case-sensitive.
 *
 * @param stringToUpdate the string to replace the last occurrence in.
 * @param searchString the substring to search for a last instance of within the string.
 * @param replacementString the string to insert into the update string should a match be found.
 *
 * @return a new string with the last occurrence of the search string replaced, if a match is found.
 */
export function replaceLast(
  stringToUpdate: string | null | undefined,
  searchString: string,
  replacementString: string
): string {
  if (stringToUpdate === null || stringToUpdate === undefined) {
    return '';
  }

  const lastIndex = stringToUpdate.lastIndexOf(searchString);

  if (lastIndex >= 0) {
    return (
      stringToUpdate.substring(0, lastIndex) +
      replacementString +
      stringToUpdate.substring(lastIndex + searchString.length)
    );
  }

  return stringToUpdate as string;
}
